import { X as CloseIcon, TrendingUp, Activity, Scale } from 'lucide-react';
import { PacienteVfp } from '../services/medicoVfpService';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';

interface PercentilModalProps {
  open: boolean;
  onClose: () => void;
  paciente: PacienteVfp | null;
}

interface EdadMeses {
  anos: number;
  meses: number;
  totalMeses: number;
}

// Datos de percentiles OMS para niños (0-5 años) - Peso para la edad (kg)
// Percentiles: P3, P15, P50, P85, P97
const percentilesPesoNinos = [
  { meses: 0, p3: 2.5, p15: 2.9, p50: 3.3, p85: 3.9, p97: 4.4 },
  { meses: 1, p3: 3.4, p15: 3.9, p50: 4.5, p85: 5.1, p97: 5.8 },
  { meses: 2, p3: 4.3, p15: 4.9, p50: 5.6, p85: 6.3, p97: 7.1 },
  { meses: 3, p3: 5.0, p15: 5.7, p50: 6.4, p85: 7.2, p97: 8.0 },
  { meses: 6, p3: 6.4, p15: 7.3, p50: 7.9, p85: 8.8, p97: 9.8 },
  { meses: 9, p3: 7.1, p15: 8.0, p50: 8.9, p85: 9.9, p97: 10.9 },
  { meses: 12, p3: 7.7, p15: 8.6, p50: 9.6, p85: 10.8, p97: 11.8 },
  { meses: 18, p3: 8.4, p15: 9.4, p50: 10.9, p85: 12.3, p97: 13.7 },
  { meses: 24, p3: 9.7, p15: 10.8, p50: 12.2, p85: 13.6, p97: 15.3 },
  { meses: 36, p3: 11.3, p15: 12.7, p50: 14.3, p85: 16.2, p97: 18.3 },
  { meses: 48, p3: 12.7, p15: 14.4, p50: 16.3, p85: 18.8, p97: 21.2 },
  { meses: 60, p3: 14.1, p15: 16.0, p50: 18.3, p85: 21.2, p97: 24.1 },
  { meses: 72, p3: 15.9, p15: 18.0, p50: 20.7, p85: 24.1, p97: 27.6 },
  { meses: 84, p3: 17.7, p15: 20.3, p50: 23.3, p85: 27.4, p97: 31.5 },
  { meses: 96, p3: 19.6, p15: 22.7, p50: 26.0, p85: 31.0, p97: 35.8 },
  { meses: 108, p3: 21.7, p15: 25.3, p50: 29.1, p85: 35.1, p97: 40.9 },
  { meses: 120, p3: 24.0, p15: 28.2, p50: 32.5, p85: 39.8, p97: 46.9 },
  { meses: 132, p3: 26.7, p15: 31.5, p50: 36.5, p85: 45.3, p97: 53.9 },
  { meses: 144, p3: 29.9, p15: 35.4, p50: 41.3, p85: 51.8, p97: 62.0 },
  { meses: 156, p3: 33.6, p15: 39.9, p50: 46.8, p85: 59.1, p97: 71.3 },
  { meses: 168, p3: 37.7, p15: 44.9, p50: 52.6, p85: 66.3, p97: 79.9 },
  { meses: 180, p3: 42.0, p15: 50.0, p50: 58.0, p85: 72.4, p97: 86.0 },
];

// Datos de percentiles OMS para niñas (0-5 años) - Peso para la edad (kg)
const percentilesPesoNinas = [
  { meses: 0, p3: 2.4, p15: 2.8, p50: 3.2, p85: 3.7, p97: 4.2 },
  { meses: 1, p3: 3.2, p15: 3.6, p50: 4.2, p85: 4.8, p97: 5.5 },
  { meses: 2, p3: 3.9, p15: 4.5, p50: 5.1, p85: 5.8, p97: 6.6 },
  { meses: 3, p3: 4.5, p15: 5.2, p50: 5.8, p85: 6.6, p97: 7.5 },
  { meses: 6, p3: 5.7, p15: 6.5, p50: 7.3, p85: 8.2, p97: 9.3 },
  { meses: 9, p3: 6.4, p15: 7.3, p50: 8.2, p85: 9.3, p97: 10.5 },
  { meses: 12, p3: 7.0, p15: 8.0, p50: 8.9, p85: 10.1, p97: 11.3 },
  { meses: 18, p3: 7.7, p15: 8.8, p50: 10.2, p85: 11.6, p97: 13.2 },
  { meses: 24, p3: 9.0, p15: 10.2, p50: 11.5, p85: 13.0, p97: 14.8 },
  { meses: 36, p3: 10.8, p15: 12.2, p50: 13.9, p85: 15.8, p97: 18.1 },
  { meses: 48, p3: 12.3, p15: 14.0, p50: 16.0, p85: 18.5, p97: 21.5 },
  { meses: 60, p3: 13.7, p15: 15.8, p50: 18.2, p85: 21.2, p97: 24.9 },
  { meses: 72, p3: 15.3, p15: 17.9, p50: 20.6, p85: 24.5, p97: 29.3 },
  { meses: 84, p3: 17.1, p15: 20.3, p50: 23.5, p85: 28.4, p97: 34.4 },
  { meses: 96, p3: 19.1, p15: 23.0, p50: 26.9, p85: 32.8, p97: 40.3 },
  { meses: 108, p3: 21.4, p15: 26.0, p50: 30.7, p85: 37.7, p97: 46.9 },
  { meses: 120, p3: 24.0, p15: 29.3, p50: 34.7, p85: 42.8, p97: 53.5 },
  { meses: 132, p3: 27.0, p15: 33.0, p50: 39.2, p85: 48.1, p97: 59.7 },
  { meses: 144, p3: 30.3, p15: 37.0, p50: 43.8, p85: 52.6, p97: 64.0 },
  { meses: 156, p3: 33.9, p15: 41.0, p50: 47.8, p85: 55.9, p97: 66.4 },
  { meses: 168, p3: 37.4, p15: 44.6, p50: 51.1, p85: 58.3, p97: 67.9 },
  { meses: 180, p3: 40.6, p15: 47.6, p50: 53.7, p85: 60.0, p97: 68.7 },
];

// Datos de percentiles OMS - Talla para la edad (cm)
const percentilesTallaNinos = [
  { meses: 0, p3: 46.1, p15: 48.0, p50: 49.9, p85: 51.8, p97: 53.7 },
  { meses: 1, p3: 50.8, p15: 52.8, p50: 54.7, p85: 56.7, p97: 58.6 },
  { meses: 2, p3: 54.4, p15: 56.4, p50: 58.4, p85: 60.4, p97: 62.4 },
  { meses: 3, p3: 57.3, p15: 59.4, p50: 61.4, p85: 63.5, p97: 65.5 },
  { meses: 6, p3: 63.3, p15: 65.5, p50: 67.6, p85: 69.8, p97: 71.9 },
  { meses: 9, p3: 67.7, p15: 70.1, p50: 72.0, p85: 74.2, p97: 76.5 },
  { meses: 12, p3: 71.0, p15: 73.4, p50: 75.7, p85: 78.1, p97: 80.5 },
  { meses: 18, p3: 76.0, p15: 78.7, p50: 81.3, p85: 83.9, p97: 86.5 },
  { meses: 24, p3: 81.7, p15: 84.5, p50: 87.1, p85: 90.0, p97: 92.9 },
  { meses: 36, p3: 88.7, p15: 92.4, p50: 96.1, p85: 99.8, p97: 103.5 },
  { meses: 48, p3: 94.9, p15: 99.1, p50: 103.3, p85: 107.5, p97: 111.7 },
  { meses: 60, p3: 100.7, p15: 105.3, p50: 109.9, p85: 114.5, p97: 119.1 },
  { meses: 72, p3: 106.1, p15: 111.2, p50: 116.4, p85: 121.7, p97: 127.0 },
  { meses: 84, p3: 111.2, p15: 116.8, p50: 122.6, p85: 128.5, p97: 134.5 },
  { meses: 96, p3: 116.1, p15: 122.3, p50: 128.6, p85: 135.1, p97: 141.8 },
  { meses: 108, p3: 120.8, p15: 127.6, p50: 134.5, p85: 141.6, p97: 148.8 },
  { meses: 120, p3: 125.4, p15: 132.7, p50: 140.3, p85: 148.1, p97: 156.2 },
  { meses: 132, p3: 130.0, p15: 137.9, p50: 146.4, p85: 155.1, p97: 164.1 },
  { meses: 144, p3: 134.9, p15: 143.6, p50: 152.8, p85: 162.3, p97: 172.0 },
  { meses: 156, p3: 140.2, p15: 149.7, p50: 159.4, p85: 169.3, p97: 179.5 },
  { meses: 168, p3: 145.7, p15: 155.7, p50: 165.7, p85: 175.7, p97: 185.7 },
  { meses: 180, p3: 150.7, p15: 160.9, p50: 171.0, p85: 181.2, p97: 191.4 },
];

const percentilesTallaNinas = [
  { meses: 0, p3: 45.4, p15: 47.3, p50: 49.1, p85: 51.0, p97: 52.9 },
  { meses: 1, p3: 49.8, p15: 51.7, p50: 53.7, p85: 55.6, p97: 57.6 },
  { meses: 2, p3: 53.0, p15: 55.0, p50: 57.1, p85: 59.1, p97: 61.1 },
  { meses: 3, p3: 55.6, p15: 57.7, p50: 59.8, p85: 61.9, p97: 64.0 },
  { meses: 6, p3: 61.2, p15: 63.5, p50: 65.7, p85: 68.0, p97: 70.3 },
  { meses: 9, p3: 65.3, p15: 67.7, p50: 70.1, p85: 72.6, p97: 75.0 },
  { meses: 12, p3: 68.9, p15: 71.4, p50: 74.0, p85: 76.6, p97: 79.2 },
  { meses: 18, p3: 74.0, p15: 76.9, p50: 79.9, p85: 82.9, p97: 85.9 },
  { meses: 24, p3: 80.0, p15: 83.2, p50: 86.4, p85: 89.6, p97: 92.9 },
  { meses: 36, p3: 87.4, p15: 91.7, p50: 95.6, p85: 99.7, p97: 103.9 },
  { meses: 48, p3: 94.1, p15: 98.9, p50: 103.3, p85: 108.1, p97: 113.0 },
  { meses: 60, p3: 99.9, p15: 105.4, p50: 110.4, p85: 115.7, p97: 121.1 },
  { meses: 72, p3: 105.4, p15: 111.8, p50: 117.6, p85: 123.7, p97: 130.0 },
  { meses: 84, p3: 110.8, p15: 118.0, p50: 124.6, p85: 131.5, p97: 138.6 },
  { meses: 96, p3: 116.1, p15: 124.2, p50: 131.7, p85: 139.5, p97: 147.5 },
  { meses: 108, p3: 121.6, p15: 130.5, p50: 138.8, p85: 147.4, p97: 156.2 },
  { meses: 120, p3: 127.3, p15: 136.9, p50: 145.9, p85: 155.0, p97: 164.3 },
  { meses: 132, p3: 133.0, p15: 143.2, p50: 152.6, p85: 162.1, p97: 171.7 },
  { meses: 144, p3: 138.2, p15: 148.7, p50: 158.6, p85: 168.1, p97: 177.6 },
  { meses: 156, p3: 142.2, p15: 152.6, p50: 162.5, p85: 172.0, p97: 181.2 },
  { meses: 168, p3: 144.8, p15: 154.6, p50: 164.3, p85: 173.9, p97: 183.3 },
  { meses: 180, p3: 146.2, p15: 155.8, p50: 165.3, p85: 174.8, p97: 184.3 },
];

export default function PercentilModal({ open, onClose, paciente }: PercentilModalProps) {
  if (!open || !paciente) {
    return null;
  }

  // Calcular edad del paciente
  const calcularEdad = (fechaNacimiento: Date | string | null | undefined): EdadMeses | null => {
    if (!fechaNacimiento) return null;
    
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    
    if (isNaN(nacimiento.getTime())) return null;
    
    let anos = hoy.getFullYear() - nacimiento.getFullYear();
    let meses = hoy.getMonth() - nacimiento.getMonth();
    
    if (meses < 0) {
      anos--;
      meses += 12;
    }
    
    const totalMeses = anos * 12 + meses;
    
    return { anos, meses, totalMeses };
  };

  // Calcular IMC
  const calcularIMC = (peso: number | null | undefined, talla: number | null | undefined): number | null => {
    if (!peso || !talla || talla === 0) return null;
    // IMC = peso (kg) / (talla (m))^2
    const tallaMetros = talla / 100;
    return peso / (tallaMetros * tallaMetros);
  };

  const edad = calcularEdad(paciente.nacimiento);
  const imc = calcularIMC(paciente.peso, paciente.talla);
  const sexo = paciente.sexo?.toUpperCase();
  const esNino = sexo === 'H' || sexo === 'M';

  // Seleccionar datos de percentiles según sexo
  const percentilesPeso = sexo === 'H' ? percentilesPesoNinos : percentilesPesoNinas;
  const percentilesTalla = sexo === 'H' ? percentilesTallaNinos : percentilesTallaNinas;

  // Función para determinar en qué percentil está el paciente
  const determinarPercentil = (valor: number, meses: number, datosPercentiles: any[]): { percentil: number; clasificacion: string } => {
    // Encontrar el rango de edad más cercano
    const rangoMasCercano = datosPercentiles.reduce((prev, curr) => {
      return Math.abs(curr.meses - meses) < Math.abs(prev.meses - meses) ? curr : prev;
    });

    if (valor < rangoMasCercano.p3) {
      return { percentil: 1, clasificacion: 'Muy bajo (< P3)' };
    } else if (valor < rangoMasCercano.p15) {
      return { percentil: 10, clasificacion: 'Bajo (P3-P15)' };
    } else if (valor < rangoMasCercano.p50) {
      return { percentil: 30, clasificacion: 'Normal bajo (P15-P50)' };
    } else if (valor < rangoMasCercano.p85) {
      return { percentil: 65, clasificacion: 'Normal (P50-P85)' };
    } else if (valor < rangoMasCercano.p97) {
      return { percentil: 90, clasificacion: 'Alto (P85-P97)' };
    } else {
      return { percentil: 98, clasificacion: 'Muy alto (> P97)' };
    }
  };

  const resultadoPeso = edad && paciente.peso 
    ? determinarPercentil(paciente.peso, edad.totalMeses, percentilesPeso)
    : null;

  const resultadoTalla = edad && paciente.talla 
    ? determinarPercentil(paciente.talla, edad.totalMeses, percentilesTalla)
    : null;

  // Preparar datos para las gráficas
  const datosPesoGrafica: Array<{
    edad: string;
    meses: number;
    P3: number | null;
    P15: number | null;
    P50: number | null;
    P85: number | null;
    P97: number | null;
    Paciente: number | null;
  }> = percentilesPeso.map(p => ({
    edad: `${Math.floor(p.meses / 12)}a ${p.meses % 12}m`,
    meses: p.meses,
    'P3': p.p3,
    'P15': p.p15,
    'P50': p.p50,
    'P85': p.p85,
    'P97': p.p97,
    'Paciente': null,
  }));

  const datosTallaGrafica: Array<{
    edad: string;
    meses: number;
    P3: number | null;
    P15: number | null;
    P50: number | null;
    P85: number | null;
    P97: number | null;
    Paciente: number | null;
  }> = percentilesTalla.map(p => ({
    edad: `${Math.floor(p.meses / 12)}a ${p.meses % 12}m`,
    meses: p.meses,
    'P3': p.p3,
    'P15': p.p15,
    'P50': p.p50,
    'P85': p.p85,
    'P97': p.p97,
    'Paciente': null,
  }));

  // Agregar el punto del paciente en la edad más cercana o crear un nuevo punto
  if (edad && paciente.peso) {
    // Buscar el índice más cercano a la edad del paciente
    const indiceMasCercano = datosPesoGrafica.reduce((prevIdx, curr, idx) => {
      const prevDiff = Math.abs(datosPesoGrafica[prevIdx].meses - edad.totalMeses);
      const currDiff = Math.abs(curr.meses - edad.totalMeses);
      return currDiff < prevDiff ? idx : prevIdx;
    }, 0);

    // Si la diferencia es menor a 6 meses, usar ese punto
    if (Math.abs(datosPesoGrafica[indiceMasCercano].meses - edad.totalMeses) <= 6) {
      datosPesoGrafica[indiceMasCercano].Paciente = paciente.peso;
    } else {
      // Insertar un nuevo punto para la edad exacta del paciente
      const nuevoPunto = {
        edad: `${edad.anos}a ${edad.meses}m`,
        meses: edad.totalMeses,
        'P3': null as number | null,
        'P15': null as number | null,
        'P50': null as number | null,
        'P85': null as number | null,
        'P97': null as number | null,
        'Paciente': paciente.peso,
      };
      // Encontrar la posición correcta para insertar
      const insertIdx = datosPesoGrafica.findIndex(d => d.meses > edad.totalMeses);
      if (insertIdx !== -1) {
        datosPesoGrafica.splice(insertIdx, 0, nuevoPunto);
      } else {
        datosPesoGrafica.push(nuevoPunto);
      }
    }
  }

  if (edad && paciente.talla) {
    // Buscar el índice más cercano a la edad del paciente
    const indiceMasCercano = datosTallaGrafica.reduce((prevIdx, curr, idx) => {
      const prevDiff = Math.abs(datosTallaGrafica[prevIdx].meses - edad.totalMeses);
      const currDiff = Math.abs(curr.meses - edad.totalMeses);
      return currDiff < prevDiff ? idx : prevIdx;
    }, 0);

    // Si la diferencia es menor a 6 meses, usar ese punto
    if (Math.abs(datosTallaGrafica[indiceMasCercano].meses - edad.totalMeses) <= 6) {
      datosTallaGrafica[indiceMasCercano].Paciente = paciente.talla;
    } else {
      // Insertar un nuevo punto para la edad exacta del paciente
      const nuevoPunto = {
        edad: `${edad.anos}a ${edad.meses}m`,
        meses: edad.totalMeses,
        'P3': null as number | null,
        'P15': null as number | null,
        'P50': null as number | null,
        'P85': null as number | null,
        'P97': null as number | null,
        'Paciente': paciente.talla,
      };
      // Encontrar la posición correcta para insertar
      const insertIdx = datosTallaGrafica.findIndex(d => d.meses > edad.totalMeses);
      if (insertIdx !== -1) {
        datosTallaGrafica.splice(insertIdx, 0, nuevoPunto);
      } else {
        datosTallaGrafica.push(nuevoPunto);
      }
    }
  }

  const formatSexo = (sexo?: string | null) => {
    if (!sexo) return 'No especificado';
    const s = sexo.toUpperCase();
    return s === 'H' ? 'Masculino' : s === 'M' ? 'Femenino' : sexo;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6 overflow-y-auto">
      <div className="w-full max-w-6xl rounded-lg bg-white shadow-2xl my-8">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4 bg-gradient-to-r from-purple-50 to-blue-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <TrendingUp className="w-7 h-7 text-purple-600" />
              Percentiles de Crecimiento
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {paciente.nombre} {paciente.apellido1} {paciente.apellido2} - Clave: {paciente.clave}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition"
            aria-label="Cerrar"
          >
            <CloseIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* Información del paciente */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="text-xs font-medium text-blue-600 uppercase mb-1">Edad</div>
              <div className="text-lg font-bold text-gray-900">
                {edad ? `${edad.anos} años ${edad.meses} meses` : 'No disponible'}
              </div>
              <div className="text-xs text-gray-500">{edad ? `${edad.totalMeses} meses` : ''}</div>
            </div>
            
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <div className="text-xs font-medium text-green-600 uppercase mb-1">Peso</div>
              <div className="text-lg font-bold text-gray-900">
                {paciente.peso ? `${paciente.peso} kg` : 'No registrado'}
              </div>
              {resultadoPeso && (
                <div className="text-xs text-gray-600 mt-1">{resultadoPeso.clasificacion}</div>
              )}
            </div>
            
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <div className="text-xs font-medium text-amber-600 uppercase mb-1">Talla</div>
              <div className="text-lg font-bold text-gray-900">
                {paciente.talla ? `${paciente.talla} cm` : 'No registrado'}
              </div>
              {resultadoTalla && (
                <div className="text-xs text-gray-600 mt-1">{resultadoTalla.clasificacion}</div>
              )}
            </div>
            
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
              <div className="text-xs font-medium text-purple-600 uppercase mb-1">IMC</div>
              <div className="text-lg font-bold text-gray-900">
                {imc ? imc.toFixed(2) : 'No disponible'}
              </div>
              <div className="text-xs text-gray-500">kg/m²</div>
            </div>
          </div>

          {!esNino && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-800">
                ⚠️ No se ha especificado el sexo del paciente o no es válido. Las gráficas de percentiles requieren esta información.
              </p>
            </div>
          )}

          {!edad && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-800">
                ⚠️ No se ha registrado la fecha de nacimiento del paciente.
              </p>
            </div>
          )}

          {esNino && edad && (
            <>
              {/* Gráfica de Peso para la Edad */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Scale className="w-5 h-5 text-green-600" />
                  Peso para la Edad - {formatSexo(sexo)}
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={datosPesoGrafica}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="edad" 
                        tick={{ fontSize: 11 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis 
                        label={{ value: 'Peso (kg)', angle: -90, position: 'insideLeft' }}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px' }}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="P3" stroke="#ef4444" strokeWidth={2} dot={false} name="P3 (Muy bajo)" />
                      <Line type="monotone" dataKey="P15" stroke="#f59e0b" strokeWidth={2} dot={false} name="P15 (Bajo)" />
                      <Line type="monotone" dataKey="P50" stroke="#10b981" strokeWidth={3} dot={false} name="P50 (Promedio)" />
                      <Line type="monotone" dataKey="P85" stroke="#f59e0b" strokeWidth={2} dot={false} name="P85 (Alto)" />
                      <Line type="monotone" dataKey="P97" stroke="#ef4444" strokeWidth={2} dot={false} name="P97 (Muy alto)" />
                      
                      {/* Punto del paciente */}
                      <Line 
                        type="monotone" 
                        dataKey="Paciente" 
                        stroke="#9333ea" 
                        strokeWidth={4}
                        dot={{ r: 8, fill: '#9333ea', stroke: '#fff', strokeWidth: 3 }}
                        activeDot={{ r: 10 }}
                        name="Paciente Actual"
                        connectNulls={false}
                      />
                      
                      {paciente.peso && edad && (
                        <ReferenceLine 
                          x={datosPesoGrafica.find(d => d.meses === edad.totalMeses)?.edad || `${edad.anos}a ${edad.meses}m`}
                          stroke="#9333ea" 
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          label={{ value: `Edad: ${edad.anos}a ${edad.meses}m`, position: 'top', fill: '#9333ea', fontSize: 11, fontWeight: 'bold' }}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                  {resultadoPeso && (
                    <div className="mt-4 text-center">
                      <span className="inline-block bg-purple-100 text-purple-800 px-4 py-2 rounded-full text-sm font-semibold">
                        Peso del paciente: {resultadoPeso.clasificacion}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Gráfica de Talla para la Edad */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-amber-600" />
                  Talla para la Edad - {formatSexo(sexo)}
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={datosTallaGrafica}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="edad" 
                        tick={{ fontSize: 11 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis 
                        label={{ value: 'Talla (cm)', angle: -90, position: 'insideLeft' }}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px' }}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="P3" stroke="#ef4444" strokeWidth={2} dot={false} name="P3 (Muy bajo)" />
                      <Line type="monotone" dataKey="P15" stroke="#f59e0b" strokeWidth={2} dot={false} name="P15 (Bajo)" />
                      <Line type="monotone" dataKey="P50" stroke="#10b981" strokeWidth={3} dot={false} name="P50 (Promedio)" />
                      <Line type="monotone" dataKey="P85" stroke="#f59e0b" strokeWidth={2} dot={false} name="P85 (Alto)" />
                      <Line type="monotone" dataKey="P97" stroke="#ef4444" strokeWidth={2} dot={false} name="P97 (Muy alto)" />
                      
                      {/* Punto del paciente */}
                      <Line 
                        type="monotone" 
                        dataKey="Paciente" 
                        stroke="#9333ea" 
                        strokeWidth={4}
                        dot={{ r: 8, fill: '#9333ea', stroke: '#fff', strokeWidth: 3 }}
                        activeDot={{ r: 10 }}
                        name="Paciente Actual"
                        connectNulls={false}
                      />
                      
                      {paciente.talla && edad && (
                        <ReferenceLine 
                          x={datosTallaGrafica.find(d => d.meses === edad.totalMeses)?.edad || `${edad.anos}a ${edad.meses}m`}
                          stroke="#9333ea" 
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          label={{ value: `Edad: ${edad.anos}a ${edad.meses}m`, position: 'top', fill: '#9333ea', fontSize: 11, fontWeight: 'bold' }}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                  {resultadoTalla && (
                    <div className="mt-4 text-center">
                      <span className="inline-block bg-purple-100 text-purple-800 px-4 py-2 rounded-full text-sm font-semibold">
                        Talla del paciente: {resultadoTalla.clasificacion}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Información adicional */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">📊 Interpretación de Percentiles</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li><strong>P3-P15:</strong> Por debajo del promedio, puede requerir seguimiento</li>
                  <li><strong>P15-P85:</strong> Rango normal de crecimiento</li>
                  <li><strong>P85-P97:</strong> Por encima del promedio</li>
                  <li><strong>&gt; P97:</strong> Muy por encima del promedio, puede requerir evaluación</li>
                </ul>
                <p className="text-xs text-blue-700 mt-3">
                  * Basado en estándares de crecimiento de la OMS para población pediátrica
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-medium"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
